import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import type { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import type { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { FormField } from '@adornis/formfield/form-field.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import type { ChancenwerkUser } from '@chancenwerk/shared/master-data-management/users/entities/ChancenwerkUser.js';
import type { AllPermissions } from '@chancenwerk/shared/permissions.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { firstValueFrom, takeUntil } from 'rxjs';
/**
 * @customElement chancen-component-semantic-entity-picker
 */
@customElement('chancen-component-semantic-entity-picker')
export class ChancenComponentSemanticEntityPicker<T extends MongoEntity, F extends AdornisFilter> extends FormField<T> {
  @property({ attribute: true, reflect: true, type: String }) public placeholder = '';
  @property({ attribute: true, reflect: true, type: String }) public 'placeholder-mode' = 'inline-floating';
  @property({ attribute: true, reflect: true, type: Boolean }) public controls = false;
  @property({ attribute: true, reflect: true, type: Boolean }) public clearable = true;
  @property({ attribute: false }) public filter: null | F = null;
  @property({ attribute: false }) public valueID = new RXController<Maybe<string>>(this, undefined);
  @property({ attribute: false }) public renderString: (entity: Maybe<T>) => string = entity =>
    entity ? entity.toString() : '';

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  protected _permission: null | AllPermissions | AllPermissions[] = null;

  override connectedCallback(): void {
    super.connectedCallback();

    this.valueID.observable.pipe(takeUntil(this.disconnected)).subscribe(async id => {
      if (id) {
        const operation = this._resolveID(id);
        if (!operation) throw new Error('No _getById given');

        const entity = await operation(this._selectionSet());
        this.value.next(entity);
      } else if (id === null && this.value.value) {
        this.value.next(null);
      }
    });
  }

  private _checkPermission() {
    // of no permission is given, the permission is granted
    if (!this._permission) return true;
    const permissions = Array.isArray(this._permission) ? this._permission : [this._permission];

    return permissions.every(p => this._currentUser.value?.hasPermission(p, ANY_CONTEXT));
  }

  override render() {
    let icon = '';

    if (!this._permission || (this._checkPermission() && this.controls)) {
      if (this.value.value) {
        icon = 'edit';
      } else {
        icon = 'add';
      }
    }

    return html`
      <cr-flex horizontal crossaxis-center>
        <cr-dropdown-search-selection
          ?disabled=${this.disabled}
          flex
          placeholder=${this.placeholder}
          placeholder-mode=${this['placeholder-mode']}
          ?clearable=${this.clearable}
          icon=${icon}
          .renderString=${this.renderString}
          .search=${this._search.bind(this)}
          .value=${this.value.value}
          @value-picked=${(e: ValueEvent<Maybe<T>>) => {
            this.value.next(e.detail.value);
            const event = new CustomEvent('value-picked', { detail: { value: e.detail.value } });
            this.dispatchEvent(event);
          }}
          @icon-click=${() => {
            if (!this.controls) return;
            if (this.value.value) {
              this._controlEdit(this.value.value);
            } else {
              this._controlNew();
            }
          }}
          @cleared=${() => {
            this.value.next(null);
            const event = new CustomEvent('cleared');
            this.dispatchEvent(event);
          }}
        ></cr-dropdown-search-selection>
      </cr-flex>
    `;
  }

  protected _subscription(): any {
    return null;
  }

  protected _filter(): any {
    return null;
  }

  protected _resolveID(id: string): null | ((gqlFields: BaseQLSelectionSet<T>) => Promise<Maybe<T>>) {
    return null;
  }

  protected _selectionSet(): any {
    return {};
  }

  protected _search(search: Maybe<string>): Promise<T[]> {
    const subscribtion = this._subscription();
    if (!subscribtion) throw new Error('No subscribtion given');
    const filterClass = this._filter();
    if (!filterClass) throw new Error('No filter given');

    const filter = new filterClass({ ...(this.filter ? this.filter.toJSON() : {}), search });

    return firstValueFrom(subscribtion(0, 20, filter)(this._selectionSet()));
  }

  protected _controlNew() {
    console.warn('No implementation for controls: new');
  }

  protected _controlEdit(entity: T) {
    console.warn('No implementation for controls: edit');
  }
}
